import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ComponentErrorBoundary, ModalProvider, Spinner } from '@/components/ui';
import { ProtectedRoute as RoleGuard } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import {
  LOANS_SIDEBAR_STORAGE_KEY,
  useSidebarShowSubItems,
} from './components/layout/useSidebarShowSubItems';
import { isLoansAppRole } from './lib/app-role';
import {
  type RouteConfig,
  SuperAdminLayout,
  SuperAdminLogin,
  appRoutes,
  superAdminRoutes,
} from './lib/route-config';
import { AuthCallback } from './pages/AuthCallback';
import { Login } from './pages/Login';
import { NotFound } from './pages/NotFound';

const LOANS_SIDEBAR_SCROLL_STORAGE_KEY = 'loans-sidebar-scroll-top';

const loadingFallback = (
  <div className="flex items-center justify-center h-64">
    <Spinner size="lg" className="h-8 w-8 text-indigo-600" />
  </div>
);

function renderRoute({ path, component: Component, roles }: RouteConfig) {
  const element = (
    <ComponentErrorBoundary name={path}>
      <Suspense fallback={loadingFallback}>
        <Component />
      </Suspense>
    </ComponentErrorBoundary>
  );

  return (
    <Route
      key={path}
      path={path}
      element={roles ? <RoleGuard roles={roles}>{element}</RoleGuard> : element}
    />
  );
}

function SelectorLaunchRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedAppRole = searchParams.get('app-role');

  localStorage.removeItem(LOANS_SIDEBAR_STORAGE_KEY);
  sessionStorage.removeItem(LOANS_SIDEBAR_SCROLL_STORAGE_KEY);
  useSidebarShowSubItems.setState({
    sessionId: null,
    bootId: null,
    openItems: {},
  });

  if (isLoansAppRole(requestedAppRole)) {
    return <Navigate to={`/dashboard?app-role=${requestedAppRole}`} replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <ModalProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Super Admin Portal — separate authentication */}
        <Route
          path="/super-admin/login"
          element={
            <Suspense fallback={loadingFallback}>
              <SuperAdminLogin />
            </Suspense>
          }
        />
        <Route
          path="/super-admin"
          element={
            <Suspense fallback={loadingFallback}>
              <SuperAdminLayout />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
          {superAdminRoutes.map(renderRoute)}
        </Route>

        {/* Authenticated routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<SelectorLaunchRedirect />} />
            {appRoutes.map(renderRoute)}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </ModalProvider>
  );
}
